import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Truck, User, FileText, AlertOctagon, AlertTriangle, Calendar, ShieldAlert, Download, Package } from "lucide-react";

const SEVERITY_STYLES = {
    "vencido": { color: "bg-red-700 text-white", icon: AlertOctagon, label: "VENCIDO" },
    "crítico": { color: "bg-red-500 text-white", icon: AlertOctagon, label: "≤ 7 días" },
    "alerta": { color: "bg-amber-500 text-white", icon: AlertTriangle, label: "≤ 30 días" },
    "próximo": { color: "bg-slate-200 text-slate-700", icon: Calendar, label: "Próximo" },
};

const CATEGORY_ICONS = {
    unidad: Truck,
    personal: User,
    convenio: FileText,
    inventario: Package,
};

const CATEGORY_LINKS = {
    unidad: "/units",
    personal: "/personnel",
    convenio: "/agreements",
    inventario: "/inventory",
};

const PERIODS = [
    { value: 30, label: "30 días" },
    { value: 60, label: "60 días" },
    { value: 90, label: "90 días" },
    { value: 180, label: "180 días" },
];

export default function Expirations() {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(60);
    const [filter, setFilter] = useState("todos");
    const [categoryFilter, setCategoryFilter] = useState("todos");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/dashboard/expirations?days=${days}`);
                setData(data);
            } catch (e) {
                console.warn(e);
            }
        })();
    }, [days]);

    if (!data) return <div className="text-sm text-slate-500 font-mono">Cargando alertas...</div>;

    const filtered = data.items.filter((x) => {
        if (filter !== "todos" && x.severity !== filter) return false;
        if (categoryFilter !== "todos" && x.category !== categoryFilter) return false;
        return true;
    });

    const exportCsv = () => {
        const rows = [["Categoría", "Sujeto", "Documento", "Vence", "Días restantes", "Severidad"]];
        filtered.forEach((it) => {
            rows.push([
                it.category,
                (it.subject || "").replace(/[\n,]/g, " "),
                (it.doc || "").replace(/[\n,]/g, " "),
                (it.date || "").slice(0, 10),
                it.days_remaining,
                it.severity,
            ]);
        });
        const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `FindMed-Vencimientos-${data.today}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-4" data-testid="expirations-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
                        <Bell className="w-6 h-6 text-red-600" /> Centro de Alertas — Vencimientos
                    </h2>
                    <div className="text-xs text-slate-500 font-mono">
                        Documentos y consumibles por vencer (pólizas, tarjetas de circulación, verificaciones, licencias, certificaciones, convenios, medicamentos e insumos caducables) · Ventana: {data.period_days} días · Corte: {data.today}
                    </div>
                    <div className="mt-2 text-[11px] text-slate-600 bg-amber-50 border-l-4 border-l-amber-400 px-3 py-2 max-w-3xl">
                        Aquí se consolidan TODOS los vencimientos próximos para que la operación no se detenga: licencias del personal, verificaciones vehiculares, seguros de ambulancias, certificaciones profesionales, contratos con clientes y caducidad de medicamentos/consumibles. Los equipos y herramientas no caducan, por eso no aparecen aquí.
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={exportCsv}
                        className="fm-btn-outline text-xs"
                        data-testid="export-expirations-csv"
                        title="Descarga la lista actual filtrada en CSV"
                    >
                        <Download className="w-3.5 h-3.5" /> Exportar CSV
                    </button>
                    <div className="flex items-center gap-1 fm-card p-1">
                        {PERIODS.map((p) => (
                            <button
                                key={p.value}
                                type="button"
                                onClick={() => setDays(p.value)}
                                data-testid={`expirations-period-${p.value}`}
                                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${days === p.value ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Severity counters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="severity-counters">
                {Object.entries(SEVERITY_STYLES).map(([sev, conf]) => {
                    const Icon = conf.icon;
                    const count = data.counters[sev] || 0;
                    const active = filter === sev;
                    return (
                        <button
                            key={sev}
                            type="button"
                            onClick={() => setFilter(active ? "todos" : sev)}
                            data-testid={`severity-${sev}`}
                            className={`fm-card p-4 text-left transition-all hover:scale-[1.02] ${active ? "ring-2 ring-slate-900" : ""}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{sev.toUpperCase()}</div>
                                    <div className="font-mono font-black text-3xl mt-1">{count}</div>
                                </div>
                                <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${conf.color}`}>
                                    <Icon className="w-4 h-4" />
                                </div>
                            </div>
                            <div className="text-[10px] text-slate-400 mt-1">{conf.label}</div>
                        </button>
                    );
                })}
            </div>

            {/* Category filters */}
            <div className="fm-card p-1 inline-flex items-center gap-1" data-testid="category-filters">
                {[
                    { id: "todos", label: "Todos", icon: ShieldAlert, count: data.total },
                    { id: "unidad", label: "Unidades", icon: Truck, count: data.by_category.unidad },
                    { id: "personal", label: "Personal", icon: User, count: data.by_category.personal },
                    { id: "convenio", label: "Convenios", icon: FileText, count: data.by_category.convenio },
                    { id: "inventario", label: "Inventario", icon: Package, count: data.by_category.inventario || 0 },
                ].map((c) => {
                    const Icon = c.icon;
                    const active = categoryFilter === c.id;
                    return (
                        <button
                            key={c.id}
                            type="button"
                            onClick={() => setCategoryFilter(c.id)}
                            data-testid={`category-${c.id}`}
                            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm flex items-center gap-1.5 transition-colors ${active ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
                        >
                            <Icon className="w-3 h-3" /> {c.label}
                            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${active ? "bg-red-600 text-white" : "bg-slate-200 text-slate-700"}`}>{c.count}</span>
                        </button>
                    );
                })}
            </div>

            {/* List */}
            <div className="fm-card overflow-hidden">
                {filtered.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-500" data-testid="expirations-empty">
                        <Bell className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />
                        Sin alertas para este filtro.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                {["Categoría", "Sujeto", "Documento", "Vence", "Días", "Severidad"].map((h) => (
                                    <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((item, i) => {
                                const SevIcon = SEVERITY_STYLES[item.severity]?.icon || Calendar;
                                const sevConf = SEVERITY_STYLES[item.severity];
                                const CatIcon = CATEGORY_ICONS[item.category] || ShieldAlert;
                                return (
                                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`expiration-row-${i}`}>
                                        <td className="py-2 px-3">
                                            <Link to={CATEGORY_LINKS[item.category] || "#"} className="inline-flex items-center gap-1.5 text-xs uppercase font-bold tracking-wider text-slate-700 hover:text-red-600">
                                                <CatIcon className="w-3.5 h-3.5" /> {item.category}
                                            </Link>
                                        </td>
                                        <td className="py-2 px-3 font-medium">{item.subject}</td>
                                        <td className="py-2 px-3 text-xs">{item.doc}</td>
                                        <td className="py-2 px-3 font-mono text-xs">{(item.date || "").slice(0, 10)}</td>
                                        <td className={`py-2 px-3 font-mono text-xs font-bold ${item.days_remaining < 0 ? "text-red-700" : item.days_remaining <= 7 ? "text-red-500" : item.days_remaining <= 30 ? "text-amber-600" : "text-slate-500"}`}>
                                            {item.days_remaining < 0 ? `Hace ${Math.abs(item.days_remaining)}d` : `${item.days_remaining}d`}
                                        </td>
                                        <td className="py-2 px-3">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] uppercase font-bold tracking-wider ${sevConf?.color}`}>
                                                <SevIcon className="w-3 h-3" /> {sevConf?.label}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
